import { $, $$ } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

function init() {
  if (!$('[data-au-input-range]')) return;

  addEventListeners();
}

function addEventListeners() {
  $$('[data-au-input-range]').forEach((container) => {
    const rangeInput = container.querySelector('input[type="range"]');
    const numberInput = container.querySelector('input[type="number"]');
    const helpText = container.querySelector('[data-au-input-range-help-text]');
    const maxOutput = parseFloat(rangeInput.getAttribute('max'));
    const minOutput = parseFloat(rangeInput.getAttribute('min'));
    const step = parseFloat(rangeInput.getAttribute('step')) || 1;

    setupSlider(minOutput, maxOutput, step, rangeInput, numberInput, helpText);
  });
}

function getDynamicSteps(minOutput, maxOutput, step) {
  return Math.min(maxOutput / step, 50); // Adjust dynamic steps according to the step
}

function getExponentialValues(minOutput, maxOutput, step) {
  const steps = getDynamicSteps(minOutput, maxOutput, step);
  const values = [];
  const factor = Math.exp(Math.log(maxOutput / Math.max(minOutput, 1)) / (steps - 1)); // Avoid division by 0

  for (let i = 0; i < steps; i++) {
    let value = parseFloat(Math.max(minOutput, 1 * Math.pow(factor, i)).toFixed(2)); // Use Math.max to avoid NaN
    if (values.length > 0 && value <= values[values.length - 1]) {
      value = parseFloat((values[values.length - 1] + step).toFixed(2));
    }

    values.push(value);
  }

  values[0] = minOutput; // Ensure the first value is exactly minOutput
  values[values.length - 1] = maxOutput; // Ensure the last value is exactly maxOutput
  return values;
}

function setupSlider(minOutput, maxOutput, step, rangeInput, numberInput, helpText) {
  const values = getExponentialValues(minOutput, maxOutput, step);
  const maxRangeValue = parseFloat(rangeInput.getAttribute('max')); // Keep the maximum value of the range
  const isStepDecimal = step % 1 !== 0; // Check if the step has decimals

  function formatValue(value) {
    const formattedValue = isStepDecimal ? value.toFixed(2) : Math.round(value);
    return formattedValue.toString().endsWith('.00') ? parseInt(formattedValue, 10) : formattedValue; // Convert to string before using endsWith
  }

  function updateNumber() {
    numberInput.value = rangeInput.value;
  }

  function updateRange() {
    const value = parseFloat(numberInput.value);
    const index = values.findIndex((v) => v >= value);
    rangeInput.value = (index / (values.length - 1)) * maxRangeValue;
  }

  function limitValues() {
    let value = parseFloat(numberInput.value);
    if (value > maxOutput || value < minOutput || numberInput.value === '') {
      value = value > maxOutput ? maxOutput : minOutput;

      numberInput.value = formatValue(value);

      // Show help text
      if (helpText) {
        helpText.classList.remove('au-hide');
        helpText.setAttribute('aria-hidden', false);
        setTimeout(() => {
          helpText.classList.remove('au-fade-out');
          helpText.classList.add('au-hide');
          helpText.setAttribute('aria-hidden', true);
        }, 2000);
      }
    }

    const index = values.findIndex((v) => v >= value);
    rangeInput.value = (index / (values.length - 1)) * maxRangeValue;
  }

  rangeInput.addEventListener('click', (e) => {
    const rangeLabel = removeAccentMarkAndUppercase(
      e.currentTarget.closest('[data-au-input-range]').querySelector('label').innerText,
    );
    const rangeValue = e.currentTarget.value;

    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickModulo',
      item_id: `${rangeLabel}_${rangeValue}`,
    });
  });

  rangeInput.addEventListener('input', () => {
    if (numberInput) {
      updateNumber();
    }
    updateSliderGradientAndTooltip(rangeInput, values);
  });

  if (numberInput) {
    numberInput.addEventListener('input', () => {
      updateRange();
      updateSliderGradientAndTooltip(rangeInput, values);
    });
  }

  if (numberInput) {
    numberInput.addEventListener('blur', () => {
      limitValues();
      updateSliderGradientAndTooltip(rangeInput, values);
    });
  }

  if (numberInput) {
    updateRange();
  }
  updateSliderGradientAndTooltip(rangeInput, values);
}

function updateSliderGradientAndTooltip(el, values) {
  const maxRangeValue = parseFloat(el.getAttribute('max'));
  const step = parseFloat(el.getAttribute('step')) || 1;
  const minRangeValue = parseFloat(el.getAttribute('min')) || 0;
  const percentage = ((el.value - minRangeValue) / (maxRangeValue - minRangeValue)) * 100;
  const rangeTooltip = el.closest('[data-au-input-range]').querySelector('[data-au-input-range-tooltip]');

  const pxFromLeft = `${(percentage * el.offsetWidth) / 100 - (percentage * rangeTooltip.offsetWidth) / 125}px`;
  const isStepDecimal = step % 1 !== 0; // Check if the step has decimals
  const index = Math.round((el.value / maxRangeValue) * (values.length - 1));
  let tooltipValue = isStepDecimal ? values[index].toFixed(2) : Math.round(values[index]);
  tooltipValue = tooltipValue.toString().endsWith('.00') ? parseInt(tooltipValue, 10) : tooltipValue; // Convert to string before using endsWith

  // set tooltip value
  rangeTooltip.textContent = tooltipValue;
  // set gradient width
  el.style.setProperty('--gradient-width', `${100 - percentage}%`);
  // set tooltip position
  rangeTooltip.style.setProperty('left', pxFromLeft);
}

export default { init };
